/**
 * Saving and loading a session.
 *
 * A GameState is plain data by construction — numbers, strings, arrays and
 * records, with the PRNG's own state carried inside it (spec §11). There is
 * nothing to serialise around: JSON is a faithful round trip, and a loaded
 * session continues bit-for-bit identically to one that was never interrupted,
 * which is the same property the determinism fixture tests.
 *
 * UI ONLY. Nothing under src/sim/ knows this file exists; the simulation has no
 * concept of a save, and the boundary check would fail if it did.
 *
 * EVERY STORAGE ACCESS IS GUARDED. localStorage can be absent, disabled, full,
 * or hold something a previous version of the game wrote. A save feature that
 * takes the page down when the quota is exceeded is worse than no save feature.
 */

import type { GameState } from '../sim/types';

const SAVE_KEY = 'heliocenter.save';

/**
 * Bump this whenever the shape of GameState changes in a way that would make an
 * older save load into a broken game. A save from a different version is
 * refused rather than half-read: a colony missing a field it needs is a much
 * worse outcome than being told to start again.
 */
const SAVE_VERSION = 1;

interface SaveEnvelope {
  version: number;
  /** Wall-clock stamp, for the menu to say how old a save is. */
  savedAt: number;
  state: GameState;
}

export interface SaveInfo {
  savedAt: number;
  tick: number;
}

export type SaveOutcome =
  | { ok: true }
  | { ok: false; reason: string };

export type LoadOutcome =
  | { ok: true; state: GameState }
  | { ok: false; reason: string };

/**
 * Is this plausibly a GameState?
 *
 * Not a full schema check — that would be a second copy of the type, drifting
 * from the first. It confirms the handful of fields the game reads before it
 * can render anything, so a corrupt or foreign save is refused with a message
 * rather than crashing three frames later somewhere unrelated.
 */
function looksLikeState(value: unknown): value is GameState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<GameState>;
  return (
    typeof candidate.tick === 'number' &&
    typeof candidate.seed === 'number' &&
    typeof candidate.planet === 'object' &&
    candidate.planet !== null &&
    Array.isArray(candidate.drones) &&
    typeof candidate.city === 'object' &&
    candidate.city !== null &&
    typeof candidate.inventory === 'object' &&
    candidate.inventory !== null
  );
}

export function saveGame(state: GameState, now: number): SaveOutcome {
  const envelope: SaveEnvelope = { version: SAVE_VERSION, savedAt: now, state };
  try {
    window.localStorage.setItem(SAVE_KEY, JSON.stringify(envelope));
    return { ok: true };
  } catch (error) {
    // Overwhelmingly this is the quota: a full world is a large object and a
    // browser in private mode may allow almost nothing.
    const reason =
      error instanceof Error && error.name === 'QuotaExceededError'
        ? 'not enough storage space'
        : 'storage unavailable';
    return { ok: false, reason };
  }
}

function readEnvelope(): SaveEnvelope | null {
  try {
    const raw = window.localStorage.getItem(SAVE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as SaveEnvelope;
  } catch {
    return null;
  }
}

export function loadGame(): LoadOutcome {
  const envelope = readEnvelope();
  if (envelope === null) return { ok: false, reason: 'no saved session' };
  if (envelope.version !== SAVE_VERSION) {
    return { ok: false, reason: 'saved by a different version of the game' };
  }
  if (!looksLikeState(envelope.state)) return { ok: false, reason: 'the save is unreadable' };
  return { ok: true, state: envelope.state };
}

/** What is on disk, for the menu to describe. Null when there is nothing. */
export function savedInfo(): SaveInfo | null {
  const envelope = readEnvelope();
  if (envelope === null || envelope.version !== SAVE_VERSION) return null;
  if (!looksLikeState(envelope.state)) return null;
  return { savedAt: envelope.savedAt, tick: envelope.state.tick };
}

export function clearSave(): void {
  try {
    window.localStorage.removeItem(SAVE_KEY);
  } catch {
    // Nothing to do: the caller's intent was to be rid of it either way.
  }
}
